import React from "react";
import withRouter from "components/Common/withRouter";

import {CardBody, CardHeader, Col, Row} from "reactstrap";
import {Link} from "react-router-dom";
import Card from "../../components/PurpleTheme/Card";
import GenerateToken from "../../components/Daemon/GenerateToken";
import {showNotification} from "../../components/Common/notification";

const IAM_POLICY_TEMPLATE = `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Effect": "Allow",
            "Action": [
                "lambda:CreateFunction",
                "lambda:UpdateFunctionCode",
                "lambda:GetFunctionConfiguration",
                "lambda:UpdateFunctionConfiguration",
                "lambda:GetFunction",
                "lambda:PublishVersion",
                "lambda:CreateAlias",
                "lambda:UpdateAlias",
                "lambda:InvokeFunction"
            ],
            "Resource": "arn:aws:lambda:*:*:function:temperstack-*"
        },
        {
            "Effect": "Allow",
            "Action": [
                "iam:PassRole"
            ],
            "Resource": "arn:aws:iam::*:role/*",
            "Condition": {
                "StringLike": {
                    "iam:PassedToService": "lambda.amazonaws.com"
                }
            }
        },
        {
            "Effect": "Allow",
            "Action": [
                "logs:CreateLogGroup",
                "logs:CreateLogStream",
                "logs:PutLogEvents"
            ],
            "Resource": "arn:aws:logs:*:*:*"
        }
    ]
}`;

function handleIAMPolicyCopy() {
  copyToClipboard(IAM_POLICY_TEMPLATE);
}

const copyToClipboard = (textToCopy) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(textToCopy)
      .then(() => showNotification('success', 'Copied!'))
      .catch(err => console.error('Could not copy text: ', err));
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      showNotification('success', 'Copied!')
    } catch (err) {
      console.error('Could not copy text: ', err);
    }
    document.body.removeChild(textarea);
  }
};

const DaemonOnboarding = () => {
  return (
    <React.Fragment>
      <Row className="mt-4">
        <Col lg="6">
          <div className="mb-4">
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2">
                <h3 className="">Instructions to Download Daemon</h3>
                <span
                  role="button"
                  style={{marginBottom: "2px"}}
                  id="tooltipAzure">
                  <i className="bx bx-help-circle font-size-20 text-white mb-1"/>
                </span>
              </div>
            </div>
            <h5>Please follow these steps:</h5>
          </div>
          <div className="mt-4 mb-5">
            <div className="section-box mb-2 p-1" style={{borderRadius: '8px'}}>
              <h5 className="mb-3">1. Create the IAM Policy for Temperstack to manage the Lambda functions</h5>
              <ol>
                <li>
                  Open the AWS Management Console and navigate to the <strong>IAM</strong> section. From the left-hand menu,
                  select <strong>Policies</strong>.
                </li>
                <li>
                  Click the <strong>Create Policy</strong> button to begin creating a new IAM policy.
                </li>
                <li>
                  In the policy creation workflow, go to the <strong>JSON</strong> tab to define the policy.
                </li>
                <li>
                  Copy the JSON policy template provided below and paste it into the <strong>JSON</strong> editor.
                </li>
                <li>
                  Click on next and give a name to the policy and create the policy.
                </li>
              </ol>
            </div>
            <div className="section-box mb-2 p-1" style={{borderRadius: '8px'}}>
              <h5 className="mb-3">2. Create the IAM Role</h5>
              <ol>
                <li>
                  Go back to the <strong>IAM</strong> section in the AWS Management Console and select <strong>Roles</strong> from the left-hand menu.
                </li>
                <li>
                  Click the <strong>Create role</strong> button to start the role creation process.
                </li>
                <li>
                  In the <strong>Trusted entity type</strong> section, choose <strong>AWS service</strong> as the entity type.
                </li>
                <li>
                  Under the <strong>Use Case</strong> section select <i>EC2</i>
                </li>
                <li>
                  Click <strong>Next</strong> to proceed to the permissions assignment step.
                </li>
                <li>
                  On the permissions screen, locate the IAM policy you created earlier. You can find it by searching for its name in the list. Select the policy to attach it to the role.
                </li>
                <li>
                  Complete the process by clicking the <strong>Create role</strong> button.
                </li>
              </ol>
            </div>
            <div className="section-box mb-2 p-1" style={{borderRadius: '8px'}}>
              <h5 className="mb-3">3. Assign the Created Role to the instance</h5>
              <ol>
                <li>
                  Go to the <strong>EC2</strong> section in the AWS Management Console and select instance in which the daemon will be installed in.
                </li>
                <li>
                  Under <strong>Actions</strong>, <strong>Security</strong>, select <strong>Modify IAM role</strong>.
                </li>
                <li>
                  Select the IAM role you created earlier from the drop-down list, and click <strong>Update IAM Role</strong>.
                </li>
              </ol>
            </div>
            <div className="section-box mb-2 p-1" style={{borderRadius: '8px'}}>
              <h5 className="mb-3">4. Install the Daemon</h5>
                <ol>
                  <li>
                    <span>Create a new directory</span>
                    <pre>
                      <code className="code-snippet">mkdir ~/.temperstack</code>
                      <i
                        className="bx bx-copy-alt"
                        style={{
                          textAlign: 'center',
                          marginLeft: 3,
                          color: 'white',
                          verticalAlign: 'middle',
                          fontSize: 'normal',
                          cursor: 'pointer',
                          width: '15px',
                          height: '15px'
                        }}
                        onClick={() => copyToClipboard("mkdir ~/.temperstack")}
                      />
                    </pre>
                  </li>
                  <li>
                    <span>Navigate to the new directory</span>
                    <pre>
                      <code className="code-snippet">cd ~/.temperstack</code>
                      <i
                        className="bx bx-copy-alt"
                        style={{
                          textAlign: 'center',
                          marginLeft: 3,
                          color: 'white',
                          verticalAlign: 'middle',
                          fontSize: 'normal',
                          cursor: 'pointer',
                          width: '15px',
                          height: '15px'
                        }}
                        onClick={() => copyToClipboard("cd ~/.temperstack")}
                      />
                    </pre>
                  </li>
                  <li>
                    <span>Download the setup script</span>
                    <pre>
                      <code className="code-snippet">curl -o setup.sh https://temperstack.s3.ap-south-1.amazonaws.com/daemon_setup.sh</code>
                      <i
                        className="bx bx-copy-alt"
                        style={{
                          textAlign: 'center',
                          marginLeft: 3,
                          color: 'white',
                          verticalAlign: 'middle',
                          fontSize: 'normal',
                          cursor: 'pointer',
                          width: '15px',
                          height: '15px'
                        }}
                        onClick={() => copyToClipboard("curl -o setup.sh https://temperstack.s3.ap-south-1.amazonaws.com/daemon_setup.sh")}
                      />
                    </pre>
                  </li>
                  <li>
                    <span>Make the script executable</span>
                    <pre>
                      <code className="code-snippet">chmod +x setup.sh</code>
                      <i
                        className="bx bx-copy-alt"
                        style={{
                          textAlign: 'center',
                          marginLeft: 3,
                          color: 'white',
                          verticalAlign: 'middle',
                          fontSize: 'normal',
                          cursor: 'pointer',
                          width: '15px',
                          height: '15px'
                        }}
                        onClick={() => copyToClipboard("chmod +x setup.sh")}
                      />
                    </pre>
                  </li>
                  {/*<li>*/}
                  {/*  <span>Download the sample configuration file</span>*/}
                  {/*  <pre>*/}
                  {/*    <code className="code-snippet">curl -o config.ini https://temperstack.s3.ap-south-1.amazonaws.com/sample-config.ini</code>*/}
                  {/*    <i*/}
                  {/*      className="bx bx-copy-alt"*/}
                  {/*      style={{*/}
                  {/*        textAlign: 'center',*/}
                  {/*        marginLeft: 3,*/}
                  {/*        color: 'white',*/}
                  {/*        verticalAlign: 'middle',*/}
                  {/*        fontSize: 'normal',*/}
                  {/*        cursor: 'pointer',*/}
                  {/*        width: '15px',*/}
                  {/*        height: '15px'*/}
                  {/*      }}*/}
                  {/*      onClick={() => copyToClipboard("curl -o config.ini https://temperstack.s3.ap-south-1.amazonaws.com/sample-config.ini")}*/}
                  {/*    />*/}
                  {/*  </pre>*/}
                  {/*  <span><strong>Note:</strong> Make sure to update the placeholder values in <code>config.ini</code> with actual configuration details.</span>*/}
                  {/*</li>*/}
                  <li>
                    <span>Run the setup script to install the daemon</span>
                    <pre>
                      <code className="code-snippet">./setup.sh ~/.temperstack/</code>
                      <i
                        className="bx bx-copy-alt"
                        style={{
                          textAlign: 'center',
                          marginLeft: 3,
                          color: 'white',
                          verticalAlign: 'middle',
                          fontSize: 'normal',
                          cursor: 'pointer',
                          width: '15px',
                          height: '15px'
                        }}
                        onClick={() => copyToClipboard("./setup.sh ~/.temperstack/")}
                      />
                    </pre>
                  </li>
                </ol>
            </div>
          </div>
          <Card>
            <CardHeader className="border-bottom text-uppercase">
              Review IAM policy template
              <div className="float-end">
                <button className="btn btn-primary pull-right" onClick={handleIAMPolicyCopy}>
                  Copy to Clipboard
                </button>
              </div>
            </CardHeader>
            <CardBody>
              <pre>
                <code className="code-snippet">
                  {IAM_POLICY_TEMPLATE.trim()}
                </code>
              </pre>
            </CardBody>
          </Card>
        </Col>
        <Col>
          <GenerateToken/>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default withRouter(DaemonOnboarding);

